import { useSelect } from 'downshift';
import _ from 'lodash';
import { useEffect, useRef, useState } from 'react';
import { MultiSelectItem } from '../../commonTypes/multiSelect.types';
import { Icon } from '../Icon/icon';
import { Tooltip } from '../Tooltip/Tooltip';
import { stringWithLineBreak } from '../../utils/helpers';
import { InputError } from '../InputError/InputError';
import { FormFieldProps } from '../../commonTypes/formField.types';
import { hashCode } from '../../utils/hash';
import React from 'react';

export interface IMultiSelectComboboxFormControlProps extends FormFieldProps<string[]> {
  items: MultiSelectItem[];
  textBoxDefaultText?: string;
  textBoxLabelText?: string;
  showDropdownIntroText?: boolean;
  dropdownIntroText?: string;
  labelTooltip?: string;
  labelClass?: string;
  showRemoveSelectionsButton?: boolean;
  enableShowAllOption?: boolean;
  showAllOptionText?: string;
  removeSelectionsText?: string;
  useHeadingInsteadOfLabel?: boolean;
  headingLevel?: number;
  errorMessage?: string;
  onSelectedItemsChange?: (selectedItems: MultiSelectItem[]) => void;
  showErrorMessageWithoutTouched?: boolean;
}

const SHOW_ALL_KEY: string = 'showall';

//Default props
MultiSelectComboboxFormControl.defaultProps = {
  textBoxDefaultText: '0 valda',
  textBoxLabelText: 'Välj regioner',
  showDropdownIntroText: false,
  dropdownIntroText: 'Regioner A till Ö',
  labelClass: '',
  showRemoveSelectionsButton: true,
  removeSelectionsText: 'Rensa val',
  enableShowAllOption: true,
  showAllOptionText: 'Visa alla',
  items: [],
  errorMessage: '',
  useHeadingInsteadOfLabel: false,
  headingLevel: 2,
  onSelectedItemsChange: (selectedItems) => {
    console.log(selectedItems);
  },
} as Partial<IMultiSelectComboboxFormControlProps>;

export function MultiSelectComboboxFormControl(props: IMultiSelectComboboxFormControlProps) {
  const firstUpdate = useRef(true);
  const toggleButtonRef = useRef<HTMLButtonElement>(null);
  const [internalItems, setInternalItems] = useState(
    props.enableShowAllOption
      ? [
          {
            key: SHOW_ALL_KEY,
            value: props.showAllOptionText,
          } as MultiSelectItem,
          ...props.items,
        ]
      : [...props.items]
  );
  const [touched, setTouched] = useState(false); // Track if the component has been touched

  const _errorId = hashCode(props.id || props.fieldName, 'error');
  useEffect(() => {
    const incomingList = props.enableShowAllOption
      ? [
          {
            key: SHOW_ALL_KEY,
            value: props.showAllOptionText,
          } as MultiSelectItem,
          ...props.items,
        ]
      : [...props.items];

    if (!_.isEqual(internalItems, incomingList)) {
      setInternalItems(incomingList);
    }
  }, [props.items]);

  const [selectedItems, setSelectedItems] = useState<MultiSelectItem[]>(
    internalItems.filter((x) => x.isSelectedByDefault)
  );
  useEffect(() => setSelectedItems(internalItems.filter((x) => x.isSelectedByDefault)), [internalItems]);

  //callback for selectedItems
  useEffect(() => {
    if (firstUpdate.current) {
      firstUpdate.current = false;
      return;
    }
    if (props.onSelectedItemsChange) {
      props.onSelectedItemsChange(selectedItems.filter((x) => x.key !== SHOW_ALL_KEY).slice());
    }
  }, [selectedItems]);

  const { isOpen, getToggleButtonProps, getLabelProps, getMenuProps, highlightedIndex, getItemProps } = useSelect({
    items: internalItems,
    onSelectedItemChange: ({ selectedItem }) => {
      setTouched(true); // Mark as touched when an item is selected
      if (!selectedItem) return;
      const index = selectedItems.indexOf(selectedItem);

      //handle select all
      if (props.enableShowAllOption && selectedItem.key === SHOW_ALL_KEY) {
        if (index === 0) {
          setSelectedItems([]);
          //setIsShowAllChecked(false)
        } else {
          setSelectedItems([...internalItems.slice()]);
          //setIsShowAllChecked(true)
        }
      } else {
        if (index > 0) {
          setSelectedItems([...selectedItems.slice(0, index), ...selectedItems.slice(index + 1)]);
        } else if (index === 0) {
          setSelectedItems([...selectedItems.slice(1)]);
        } else {
          setSelectedItems([...selectedItems, selectedItem]);
        }
      }
    },
    onIsOpenChange: ({ isOpen }) => {
      // When menu closes (including via Escape key), return focus to toggle button
      if (!isOpen && toggleButtonRef.current) {
        toggleButtonRef.current.focus();
      }
    },
    selectedItem: null,
    stateReducer: (state, actionAndChanges) => {
      const { changes, type } = actionAndChanges;
      switch (type) {
        case useSelect.stateChangeTypes.MenuKeyDownEnter:
        case useSelect.stateChangeTypes.MenuKeyDownSpaceButton:
        case useSelect.stateChangeTypes.ItemClick:
          return {
            ...changes,
            isOpen: true, // keep menu open after selection.
            highlightedIndex: state.highlightedIndex,
          };
        default:
          return changes;
      }
    },
  });

  const getSelectedText = (): string => {
    if (selectedItems.length === 0) {
      return `${props.textBoxDefaultText}`;
    }

    if (selectedItems.length === 1) {
      return `${selectedItems[0].value}`;
    }

    return `${selectedItems.length} valda`;
  };

  const handleDeselectAll = (e) => {
    e.preventDefault();
    setTouched(true); // Mark as touched when deselecting all
    setSelectedItems([]);
  };

  const HTag = `h${props.headingLevel}`;

  return (
    <div className="cmb-wrapper">
      <div className="cmb-wrapper__inner">
        {props.useHeadingInsteadOfLabel && (
          <HTag {...getLabelProps()} className={`${props.labelClass} inline`}>
            {props.textBoxLabelText}
          </HTag>
        )}
        {!props.useHeadingInsteadOfLabel && (
          <label {...getLabelProps()} className={props.labelClass}>
            {props.textBoxLabelText}
          </label>
        )}
        {props.labelTooltip && (
          <Tooltip text={stringWithLineBreak(props.labelTooltip)} enableEpiserverOnPageEditing={true} />
        )}
        <div className="cmb-wrapper__tools">
          <button type="button" className="cmb-wrapper__inner__btn" {...getToggleButtonProps({ ref: toggleButtonRef })}>
            {getSelectedText()}
            <Icon
              className={`cmb-wrapper__inner__btn__arrow ${isOpen ? 'cmb-wrapper__inner__btn__arrow--expanded' : ''}`}
              iconType="fontawesome/chevron-left"
            />
          </button>{' '}
          {props.showRemoveSelectionsButton && (
            <button className="cmb-wrapper__inner__clean" type="button" onClick={handleDeselectAll}>
              {props.removeSelectionsText}
            </button>
          )}
        </div>

        {isOpen && (
          <ul className={`cmb-wrapper__inner__expandable cmb-wrapper__inner__expandable--expanded`} {...getMenuProps()}>
            <>
              {props.showDropdownIntroText && props.dropdownIntroText && <span>{props.dropdownIntroText}</span>}
              {internalItems.map((item, index) => (
                <li
                  className={highlightedIndex === index ? 'cmb-wrapper__active' : ''}
                  key={`${item.key}_${index}`}
                  {...getItemProps({
                    item,
                    index,
                  })}>
                  <input
                    type="checkbox"
                    checked={selectedItems.some((x) => x.key === item.key)}
                    value={item.key}
                    onChange={() => null}
                  />
                  <span />
                  {item.value}
                </li>
              ))}
            </>
          </ul>
        )}

        {props.showErrorMessageWithoutTouched && props.errorMessage ? (
          <InputError id={_errorId} errorText={props.errorMessage} />
        ) : (
          touched && props.errorMessage && <InputError id={_errorId} errorText={props.errorMessage} />
        )}
      </div>
    </div>
  );
}
